class MediaContentSlider {
  constructor(el) {
    this.el       = el;
    this.navItems = Array.from(el.querySelectorAll('.sms__nav-item'));
    this.bullets  = Array.from(el.querySelectorAll('.sms__bullet'));
    this.visuals  = Array.from(el.querySelectorAll('.sms__visual'));
    this.total    = this.visuals.length;

    if (this.total <= 1) return;

    this.current   = 0;
    this.autoplay  = el.dataset.autoplay === 'true';
    this.delay     = parseInt(el.dataset.delay, 10) || 6000;
    this.raf       = null;
    this.startTime = null;
    this.isMobile  = this._checkMobile();

    /* Clicking a nav item or bullet navigates to that slide */
    this.navItems.forEach((item, i) => {
      item.addEventListener('click', () => this._goTo(i));
    });
    this.bullets.forEach((btn, i) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation(); /* nav-item click already handles it */
        this._goTo(i);
      });
    });

    this.resizeObserver = new ResizeObserver(() => {
      const wasMobile = this.isMobile;
      this.isMobile = this._checkMobile();
      if (wasMobile !== this.isMobile && this.autoplay) {
        this._stopProgress();
        this._startProgress();
      }
    });
    this.resizeObserver.observe(this.el);

    if (this.autoplay) this._startProgress();
  }

  destroy() {
    this._stopProgress();
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  /* ── Navigation ── */

  _goTo(index) {
    if (index === this.current) return;
    this._stopProgress();

    const prev = this.current;
    this.current = index;

    /* Only the media panel transitions */
    this.visuals[prev].classList.remove('is-active');
    this.visuals[index].classList.add('is-active');

    /* Update nav items (opacity) */
    this.navItems[prev].classList.remove('is-active');
    this.navItems[index].classList.add('is-active');

    /* Update bullets */
    this.bullets.forEach((btn, i) => {
      const active = i === index;
      btn.classList.toggle('is-active', active);
      btn.classList.toggle('is-done', i < index);
      btn.setAttribute('aria-current', active ? 'true' : 'false');
      /* Always set fill explicitly — inline style overrides CSS, so
         stale mid-animation values must be cleared on every transition */
      this._setFill(btn, i < index ? 1 : 0);
    });

    if (this.autoplay) this._startProgress();
  }

  _next() {
    this._goTo((this.current + 1) % this.total);
  }

  /* ── Progress animation ── */

  _startProgress() {
    this._stopProgress();
    this.startTime = null;

    const bullet = this.bullets[this.current];
    const delay  = this.delay;

    const tick = (timestamp) => {
      if (!this.startTime) this.startTime = timestamp;
      const progress = Math.min((timestamp - this.startTime) / delay, 1);
      this._setFill(bullet, progress);

      if (progress < 1) {
        this.raf = requestAnimationFrame(tick);
      } else {
        this._next();
      }
    };

    this.raf = requestAnimationFrame(tick);
  }

  _stopProgress() {
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    this.startTime = null;
  }

  _setFill(bullet, progress) {
    const fill = bullet.querySelector('.sms__bullet-fill');
    if (!fill) return;
    fill.style.transform = this.isMobile
      ? `scaleX(${progress})`
      : `scaleY(${progress})`;
  }

  _checkMobile() {
    return window.matchMedia('(max-width: 989px)').matches;
  }
}

/* ── Init ── */

function initMediaContentSliders() {
  document.querySelectorAll('.sms').forEach((el) => {
    if (!el._smsInstance) {
      el._smsInstance = new MediaContentSlider(el);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMediaContentSliders);
} else {
  initMediaContentSliders();
}

/* Shopify theme editor: re-init on section reload */
document.addEventListener('shopify:section:load', (e) => {
  e.target.querySelectorAll('.sms').forEach((el) => {
    if (el._smsInstance) { 
      el._smsInstance.destroy();
      el._smsInstance = null;
    }
    el._smsInstance = new MediaContentSlider(el);
  });
});
